import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useSelfServiceRefreshTokenHook } from '@/api/trafikportalen';
import { useSelfServiceAuth } from '@/stores/selfServiceAuth';

const REFRESH_BUFFER_MINUTES = 5;

export const useSelfServiceTokenRefresh = () => {
  const { token, expiresAt, setToken, isTokenValid } = useSelfServiceAuth();
  const refreshToken = useSelfServiceRefreshTokenHook();

  const getRefreshInterval = useCallback((): number | false => {
    if (!expiresAt || !token) return false;

    const expires = DateTime.fromISO(expiresAt);
    const refreshAt = expires.minus({ minutes: REFRESH_BUFFER_MINUTES });
    const msUntilRefresh = refreshAt.diffNow().toMillis();

    // If we're past the refresh threshold, refresh immediately (1 second delay)
    if (msUntilRefresh <= 0) return 1000;

    return msUntilRefresh;
  }, [expiresAt, token]);

  useQuery({
    queryKey: ['self-service-token-refresh'],
    queryFn: async () => {
      const response = await refreshToken();
      setToken(response.token, response.expires_at);
      return response;
    },
    enabled: !!token && isTokenValid(),
    refetchInterval: getRefreshInterval,
    refetchIntervalInBackground: true,
    retry: 1,
    staleTime: Infinity,
  });
};
